import { Input } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  MessageInterface,
  MessageInterfaceWithPaginationInterface
} from '../../../shared/type/message.type';
import { useAlertContext } from '../../../context/AlertProvider';
import { PaginationInterface } from '../../../shared/type/paginationInterface.type';
import IconComponent from '../../IconComponent/IconComponent';
import CustomAlert from '../../CustomAlert/CustomAlert';

interface Props {
  hideSearchInput?: boolean;
  sectionHeader: React.ReactNode;
  tenantId: string;
  setRefreshMessages: React.Dispatch<React.SetStateAction<() => void>>;
  fetchMessagesFn: (params: {
    tenantId: string;
    pageNumber: number;
    pageSize: number;
    searchPhrase: string;
  }) => Promise<MessageInterfaceWithPaginationInterface>;
  deleteMessagesFn: (messageId: number) => Promise<void>;
}

const defaultDeletePopup = { isOpen: false, messageId: 0 };

const GenericMessagePageComponent = ({
  tenantId,
  setRefreshMessages,
  fetchMessagesFn,
  deleteMessagesFn,
  sectionHeader,
  hideSearchInput = false
}: Props) => {
  const location = useLocation();
  const { setShow, setAlertProperties } = useAlertContext();
  const [messages, setMessages] = useState<MessageInterface[]>([]);
  const [searchPhrase, setSearchPhrase] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInterface>({
    totalCount: 0,
    hasNextPage: false,
    next: { limit: 0, offset: 0 }
  });
  const [deletePopup, setDeletePopup] = useState(defaultDeletePopup);
  const isAnnouncementBox = location.pathname.includes('announcement');

  const fetchMessages = async (page = currentPage, phrase = searchPhrase) => {
    try {
      const res = await fetchMessagesFn({
        tenantId,
        pageNumber: page,
        pageSize: 10,
        searchPhrase: phrase
      });

      setMessages(res.data);
      setPagination({
        totalCount: res.totalCount,
        hasNextPage: res.hasNextPage,
        next: res.next
      });
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchMessages(1, searchPhrase);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(debounce);
  }, [searchPhrase]);

  useEffect(() => {
    fetchMessages(currentPage);
  }, [currentPage, tenantId, location.pathname]);

  useEffect(() => {
    setRefreshMessages(() => () => fetchMessages(currentPage));
  }, [currentPage, setRefreshMessages]);

  const goToNextPage = () => pagination.hasNextPage && setCurrentPage((p) => p + 1);
  const goToPreviousPage = () => currentPage > 1 && setCurrentPage((p) => p - 1);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    return now.getTime() - date.getTime() < 24 * 60 * 60 * 1000
      ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString('pl-PL');
  };

  const handleDelete = useCallback(async () => {
    try {
      await deleteMessagesFn(deletePopup.messageId);
      setAlertProperties({
        status: 'success',
        timeout: 9000,
        description: 'Wiadomość usunięta',
        title: 'Sukces'
      });
    } catch {
      setAlertProperties({
        status: 'error',
        timeout: 9000,
        description: 'Nie udało się usunąć wiadomości',
        title: 'Błąd'
      });
    } finally {
      setShow(true);
      fetchMessages(currentPage);
      setDeletePopup(defaultDeletePopup);
    }
  }, [deleteMessagesFn, deletePopup.messageId]);

  return (
    <div className="ParentMessage flex flex-col">
      <div className="flex justify-between pr-spacing-3xl pl-spacing-3xl mt-spacing-xl mb-spacing-5xl">
        {sectionHeader}
      </div>
      <div className="pt-spacing-md pr-spacing-4xl pb-spacing-md pl-spacing-4xl">
        <div className={`flex ${hideSearchInput ? 'justify-end' : 'justify-between'} items-center`}>
          {!hideSearchInput && (
            <div className="w-96">
              <Input
                type="text"
                placeholder="Szukaj po adresacie lub temacie"
                value={searchPhrase}
                onChange={(e) => setSearchPhrase(e.target.value)}
              />
            </div>
          )}
          <div className="flex gap-spacing-lg items-center">
            <p className="text-sm font-medium text-grayLight-700">
              Strona {currentPage} z {Math.ceil(pagination.totalCount / 10)}
            </p>
            <div className="flex gap-spacing-lg">
              <button onClick={goToPreviousPage} disabled={currentPage === 1}>
                <IconComponent iconName="chevronLeft" className="h-5 w-5" />
              </button>
              <button onClick={goToNextPage} disabled={!pagination.hasNextPage}>
                <IconComponent iconName="chevronRight" className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col">
        {messages.length === 0 ? (
          <p className="text-center text-gray-500 mt-4">Brak wiadomości do wyświetlenia.</p>
        ) : (
          messages
            .sort(
              (a, b) =>
                new Date(b?.lastUpdate || b?.sent).getTime() -
                new Date(a?.lastUpdate || a?.sent).getTime()
            )
            .map((message) => {
              return (
                <div
                  key={message.id}
                  className={`flex pt-spacing-lg pr-spacing-4xl pb-spacing-lg pl-spacing-4xl hover:bg-brand-100 ${
                    !message.hasBeanRead && 'bg-grayLight-100'
                  }`}>
                  <Link
                    to={`${isAnnouncementBox ? '' : 'message/'}${message.id}?title=${encodeURIComponent(message.topic)}`}
                    className="basis-11/12 flex items-center gap-spacing-lg">
                    <div className="w-96 flex items-start">
                      <p
                        className={`text-lg font-${message.hasBeanRead || message.hasBeanRead === undefined ? 'normal' : 'semibold'} text-grayLight-700`}>
                        {message?.customerUser?.firstName || message?.sendingUser?.firstName}{' '}
                        {message?.customerUser?.lastName || message?.sendingUser?.lastName}
                      </p>
                    </div>
                    <p
                      className={`text-lg font-${message.hasBeanRead || message.hasBeanRead === undefined ? 'normal' : 'semibold'} text-grayLight-700`}>
                      {message.topic}
                    </p>
                  </Link>
                  <div className="basis-1/12 flex items-center justify-center group">
                    <p className="group-hover:hidden">
                      {formatDate(message?.lastUpdate || message?.sent)}
                    </p>
                    <button onClick={() => setDeletePopup({ isOpen: true, messageId: message.id })}>
                      <IconComponent
                        color="#171412"
                        iconName="trash"
                        className="hidden group-hover:block"
                      />
                    </button>
                  </div>
                </div>
              );
            })
        )}
      </div>

      <CustomAlert
        handleOpen={deletePopup.isOpen}
        icon={
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-error-100">
            <IconComponent iconName="trash" color="#D92D20" />
          </div>
        }
        header="Usunąć wiadomość?"
        content="Usunięcie wiadomości nieodwracalne."
        cancelButtonText="Anuluj"
        cancelButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'secondaryGray' }}
        confirmButtonText="Usuń"
        confirmButton={{ iconPosition: 'onlyText', size: 'lg', hierarchy: 'warning' }}
        onCancelAction={() => setDeletePopup(defaultDeletePopup)}
        onConfirmAction={handleDelete}
      />
    </div>
  );
};

export default GenericMessagePageComponent;
